import { useState, useRef, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $insertNodes, $createTextNode } from 'lexical';

import LatexRenderer from '../../../latex/renderer/LatexRenderer';
import LatexNode from '../nodes/LatexNode';
import SimpleLatexNode from '../nodes/SimpleLatexNode';
import { TEXT_COLORS } from '../utils/toolConstants';

interface InsertLatexFormProps {
    simpleMode?: boolean;
}

export default function InsertLatexForm({ simpleMode = false }: InsertLatexFormProps) {
    const [editor] = useLexicalComposerContext();
    const [latex, setLatex] = useState('');
    const [color, setColor] = useState(TEXT_COLORS[0].value);
    const [showForm, setShowForm] = useState(false);
    const formRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Close form when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (formRef.current && !formRef.current.contains(event.target as Node)) {
                setShowForm(false);
            }
        }

        if (showForm) {
            inputRef.current?.focus();
            document.addEventListener('pointerup', handleClickOutside);
        }

        return () => {
            document.removeEventListener('pointerup', handleClickOutside);
        };
    }, [showForm]);

    const insertLatex = () => {
        editor.update(() => {
            let node;
            if (simpleMode) {
                node = new SimpleLatexNode(latex);
            } else {
                node = new LatexNode(latex, color);
            }
            $insertNodes([$createTextNode(' '), node, $createTextNode(' ')]);

            setLatex('');
        });
        setShowForm(false);
    };

    return (
        <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => setShowForm(!showForm)} title="Insert LaTeX">
                ∑
            </button>

            {showForm && (
                <div
                    ref={formRef}
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        background: '#fff',
                        padding: '12px',
                        border: '1px solid #ccc',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        zIndex: 100,
                        width: 300,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                    }}
                >
                    <label style={{ display: 'flex', flexDirection: 'column' }}>
                        Formula:
                        <input
                            type="text"
                            value={latex}
                            style={{
                                border: '1px solid #ccc',
                                padding: '8px',
                            }}
                            onChange={(e) => setLatex(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    insertLatex();
                                }
                            }}
                            ref={inputRef}
                        />
                    </label>

                    <label style={{ display: 'flex', flexDirection: 'column' }}>
                        Color:
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <select value={color} onChange={(e) => setColor(e.target.value)}>
                                <option value="" disabled>
                                    Select
                                </option>
                                {TEXT_COLORS.map(({ label, value }) => (
                                    <option key={label} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                            <input type="color" value={color} disabled />
                        </div>
                    </label>

                    <div>
                        <strong>Preview:</strong>
                        <div
                            style={{
                                border: '1px solid #ddd',
                                padding: '8px',
                                marginTop: '4px',
                                minHeight: '30px',
                                color,
                                fontFamily: 'Verdana',
                            }}
                        >
                            {latex && <LatexRenderer latex={latex} color={color} />}
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={insertLatex}>Insert</button>
                    </div>
                </div>
            )}
        </div>
    );
}
